// useProfile returns { profile } as an object in the following format:
// { 'cis.profile.surname': { value: 'Doe', shared: true } }
// handleProfileChange takes the profile as an array in the following format:
// [{ name: 'cis.profile.surname', value: 'Doe', shared: false }]
// storing it as an object in state makes merging properties simple
// and easier to convert to a view object
import { useCallback, useMemo, useState } from 'react'
import axios from '@fs/zion-axios'
import { memoize } from 'lodash-es'
import { clearCachedUser } from '@fs/zion-user'
import useErrorReporter from './useErrorReporter'
import useFetchData from './useFetchData'
import cisAxiosConfig from '../cisAxiosConfig'
import throwCisError from '../services/throwCisError'
import withNoticeWarnings from '../services/withNoticeWarnings'

// cis API uses profile data in the following format:
// [{ name: 'cis.profile.surname', value: 'Doe', shared: false }]
// so convert into an object for easier access for rendering and managing state
// use object keys equal to the 'name' field:
// { 'cis.profile.surname': { value: 'Doe' } }
const formatProfileObj = (profile) => {
  return profile.reduce((previousValue, currentValue) => {
    previousValue[currentValue.name] = currentValue
    return previousValue
  }, {})
}

// simple memoization strategy for profile since it used across views but we do not
// want it to survive across page loads
let profileCacheKey = 0

// exported so testUtils can cache bust
export function invalidateUserProfileCache() {
  profileCacheKey++
}

const fetchProfile = memoize(
  async () => {
    const { data } = await withNoticeWarnings(
      axios.post(
        `/service/ident/cis/cis-public-api/v4/profileData/category`,
        {
          categories: ['common.profile', 'cis.profile'],
        },
        cisAxiosConfig
      )
    )
    const profileData = data?.profileData ?? []
    return formatProfileObj(profileData)
  },
  () => profileCacheKey
)

const updateUsernameIfNeeded = async (profileData, fullProfile) => {
  const username = profileData.find((x) => x.name === 'cis.profile.username')
  if (username) {
    const cisId = fullProfile['cis.profile.userid'].value
    await withNoticeWarnings(
      axios.post(
        `/service/ident/cis/cis-public-api/v4/user`,
        { users: [{ type: 'userAccount', id: cisId, username: username.value }] },
        cisAxiosConfig
      )
    )
  }
}

const updateProfile = async (unfilteredNewProfileData, profile) => {
  // Filter out undefined values since the backend may or may not handle them correctly depending on the specific property.
  // Theoretically, it could be useful to send the `shared` property without a `value` but in practice this is not useful.
  // Filter out null values as well just in case they make it here for some reason based on form initial values implementation.
  // Also filter out non-dirty values. This helps with some of the error message handling to pinpoint
  // which field caused an issue (e.g. AccountAlreadyExistsError). It's also more efficient.
  const profileData = unfilteredNewProfileData.filter(({ name, shared, value }) => {
    if (value === undefined || value === null) {
      return false
    }
    const readOnlyFields = ['cis.profile.userid', 'cis.profile.email.verify', 'cis.profile.sms.verify']
    if (readOnlyFields.includes(name)) {
      return false
    }
    return (
      // filter dirty except for SMS number & country as the backend doesn't handle this, IDENT-8468
      name === 'cis.profile.sms.country' ||
      name === 'cis.profile.sms.number' ||
      profile[name]?.value !== value ||
      profile[name]?.shared !== shared
    )
  })
  if (profileData.length === 0) {
    return
  }
  try {
    await withNoticeWarnings(
      axios.post(
        `/service/ident/cis/cis-public-api/v4/profileData`,
        {
          profileData,
        },
        cisAxiosConfig
      )
    )
    invalidateUserProfileCache()
    // we could optionally only clear user cache on specific properties but that would require
    // some tighter coupling so just clear it all to be safe
    clearCachedUser()
    await updateUsernameIfNeeded(profileData, profile)
  } catch (error) {
    throwCisError(error, profileData)
  }
}

const initialProfile = {}

export default function useAccount() {
  const [refreshToken, setRefreshToken] = useState(0)
  const [localAccount, setLocalAccount] = useState(null)
  const invalidateAccountCache = (keysToClear = []) => {
    invalidateUserProfileCache()
    setRefreshToken(refreshToken + 1)
    const newLocalAccount = { ...localAccount }
    keysToClear.forEach((key) => {
      delete newLocalAccount[key]
    })
    setLocalAccount(newLocalAccount)
  }
  const { data: remoteAccount, loading: accountLoading } = useFetchData(fetchProfile, {
    initialData: initialProfile,
    requestFnArg: refreshToken,
  })
  const reportError = useErrorReporter()
  const account = useMemo(() => ({ ...remoteAccount, ...localAccount }), [remoteAccount, localAccount])

  const handleChangeAccount = useCallback(
    async (rawProfileUpdateArray, { throwErrors } = {}) => {
      // Avoid processing non-existent profile data.
      // (CIS calls will fail if you try to delete non-existent profile data--IDENT-8992).
      // User still needs to be able to clear existing data though.
      const profileUpdateArray = rawProfileUpdateArray
        .filter(({ exists, value }) => exists || value)
        .map(({ name, value, shared }) => ({ name, value, shared }))
      const profileUpdateObj = formatProfileObj(profileUpdateArray)
      const emailChanged =
        Object.keys(profileUpdateObj).includes('cis.profile.email') &&
        profileUpdateObj['cis.profile.email']?.value !== account['cis.profile.email']?.value
      const smsNumberChanged =
        Object.keys(profileUpdateObj).includes('cis.profile.sms.number') &&
        (profileUpdateObj['cis.profile.sms.number']?.value !== account['cis.profile.sms.number']?.value ||
          profileUpdateObj['cis.profile.sms.country']?.value !== account['cis.profile.sms.country']?.value)
      setLocalAccount({
        ...localAccount,
        'cis.profile.email.verify': { value: emailChanged ? 'F' : account['cis.profile.email.verify']?.value },
        'cis.profile.sms.verify': { value: smsNumberChanged ? 'F' : account['cis.profile.sms.verify']?.value },
        ...profileUpdateObj,
      })
      try {
        await updateProfile(profileUpdateArray, account)
      } catch (error) {
        setLocalAccount(localAccount)
        if (throwErrors) {
          throw error
        } else {
          reportError(error)
        }
      }
    },
    [localAccount, account, reportError]
  )

  return {
    account,
    // optimistically render if we can (so loading is only for initial fetch)
    accountLoading: refreshToken === 0 ? accountLoading : false,
    handleChangeAccount,
    invalidateAccountCache,
  }
}
